import { buildQuery } from '../../api/query';
import { readAccountUsage } from '../usage/accountReader';
import {
  readArray,
  readEnum,
  readMoney,
  readNullableString,
  readRecord,
  readSecret,
  readString,
} from '../readers';
import { semanticDelete, semanticGet, semanticPost } from '../semanticRepository';
import type {
  ApiKey,
  ApiKeyPatch,
  ApiKeyUsage,
  CreateApiKeyRequest,
  KeyResetInterval,
  KeyScope,
} from './model';

const KEY_STATUSES: readonly ApiKey['status'][] = ['active', 'disabled', 'paused', 'expired', 'revoked'];
const KEY_SCOPES: readonly KeyScope[] = ['full', 'run', 'read', 'seller'];
const RESET_INTERVALS: readonly KeyResetInterval[] = ['none', 'daily', 'weekly', 'monthly'];

export const readApiKey = (value: unknown): ApiKey => {
  const row = readRecord(value, 'API key');
  const status = readEnum(row.status, KEY_STATUSES, 'API key status');
  const interval = row.limitInterval == null
    ? 'none'
    : readEnum(row.limitInterval, RESET_INTERVALS, 'key limit interval');
  const usage = row.usage30d == null ? undefined : readRecord(row.usage30d, 'key usage summary');
  return {
    id: readString(row.id, 'API key id'),
    name: readString(row.name, 'API key name'),
    maskedKey: readString(row.maskedKey, 'masked API key'),
    createdAt: readString(row.createdAt, 'key creation time'),
    lastUsedAt: readNullableString(row.lastUsedAt, 'key last-used time'),
    status,
    scope: readEnum(row.scope, KEY_SCOPES, 'API key access level'),
    limitUsd: row.limitUsd == null ? null : readMoney(row.limitUsd, 'key limit USD'),
    resetInterval: interval,
    spentUsd: readMoney(row.spentUsd, 'key spend USD'),
    expiresAt: readNullableString(row.expiresAt, 'key expiry'),
    createdBy: readNullableString(row.createdBy, 'key creator'),
    usage30d: usage && {
      costUsd: readMoney(usage.costUsd, 'key usage USD'),
    },
  };
};

const readKeys = (value: unknown): ApiKey[] =>
  readArray(readRecord(value, 'API keys response').keys, 'API keys').map(readApiKey);

const readCreatedKey = (value: unknown) => {
  const row = readRecord(value, 'created API key');
  return { secret: readSecret(row.secret, 'API key secret'), record: readApiKey(row.record) };
};

const readKeyUsage = (value: unknown): ApiKeyUsage => {
  const row = readRecord(value, 'API key usage');
  return {
    ...readAccountUsage(row),
    todayUsd: readMoney(row.todayUsd, 'today key spend'),
    thisWeekUsd: readMoney(row.thisWeekUsd, 'weekly key spend'),
    thisMonthUsd: readMoney(row.thisMonthUsd, 'monthly key spend'),
  };
};

const wireInterval = (interval?: KeyResetInterval): string | null =>
  interval && interval !== 'none' ? interval : null;

export const fetchApiKeys = () => semanticGet('/v1/api-keys', readKeys);
export const fetchApiKey = (id: string) =>
  semanticGet(`/v1/api-keys/${encodeURIComponent(id)}`, readApiKey);
export const createApiKey = (request: CreateApiKeyRequest) =>
  semanticPost(
    '/v1/api-keys',
    {
      name: request.name,
      limitUsd: request.limitUsd ?? null,
      limitInterval: wireInterval(request.resetInterval),
      expiresIn: request.expiresIn ?? 'never',
      scope: request.scope ?? 'full',
    },
    readCreatedKey,
  );
/** Replaces the key's secret in place: the old secret stops working at once;
 *  the id, name, limit, spend and access level stay. */
export const rotateApiKey = (id: string) =>
  semanticPost(`/v1/api-keys/${encodeURIComponent(id)}/rotate`, undefined, readCreatedKey);
export const updateApiKey = (id: string, patch: ApiKeyPatch) => {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.limitUsd !== undefined) {
    body.limitUsd = patch.limitUsd;
    body.limitInterval = patch.limitUsd === null ? null : wireInterval(patch.resetInterval);
  }
  if (patch.disabled !== undefined) body.disabled = patch.disabled;
  return semanticPost(`/v1/api-keys/${encodeURIComponent(id)}`, body, readApiKey);
};
export const fetchApiKeyUsage = (id: string, days = 30, timeZone?: string) =>
  semanticGet(
    `/v1/api-keys/${encodeURIComponent(id)}/usage${buildQuery({ days, tz: timeZone })}`,
    readKeyUsage,
  );
export const revokeApiKey = (id: string): Promise<void> =>
  semanticDelete(`/v1/api-keys/${encodeURIComponent(id)}`);
